import { isEqual, omit } from 'lodash';
import { limitTypes } from './components/helper';
import { DEFAULT_PRECISION } from 'helpers/constants';
import { baseToCustomUOM, getMeasurementUnits, getMeasurementUnitSymbol } from 'helpers/unitHelper';

const checkAnyLimitValueIsSame = (currentTimeSeries) => {
    let isAnySameLimit = false;
    const sameValueLimits = [];
    let updatedTimeSeries = {
        ...currentTimeSeries,
        limits: currentTimeSeries?.limits?.map((limit) => {
            // if (!limit?.isUserLimit) { // EXCLUDE PERSONAL LIMITS
            const sameLimits = currentTimeSeries?.limits?.filter((sl) => +sl?.limitValue?.toString() === +limit?.limitValue?.toString() && sl?.uniqId !== limit?.uniqId); // add && !sl?.isUserLimit if needs to exclude personal limits with same limit values
            if (sameLimits?.length > 0) {
                isAnySameLimit = true;
                for (let i = 0; i < sameLimits?.length; i++) {
                    sameValueLimits?.push({ ...sameLimits[i], isSameLimit: true });
                }
            }
            // }
            return { ...limit, isSameLimit: false };
        })
    };
    if (isAnySameLimit) {
        // UPDATE SAME VALUES LIMITS
        updatedTimeSeries = {
            ...updatedTimeSeries,
            limits: updatedTimeSeries?.limits?.map((lmt) => {
                const isLimitSame = sameValueLimits?.find((svl) => svl?.uniqId === lmt?.uniqId);
                if (isLimitSame) {
                    return isLimitSame;
                }
                return lmt;
            })
        };
    }

    return updatedTimeSeries;
};

export const updateLimitReducerData = (state, action) => {
    const { limitUniqId, field, value, currentTimeSeriesUniqId } = action.payload;

    // Find the currentTimeSeries item to update
    const currentTimeSeriesItemIndex = state.currentTimeSeries?.findIndex(item => item.uniqId === currentTimeSeriesUniqId);
    const correspondingTimeSeriesItem = state.timeSeries?.timeSeriesList?.find(item => item.uniqId === currentTimeSeriesUniqId);

    // Update the currentTimeSeries item with the payload
    const updatedCurrentTimeSeries = {
        ...state.currentTimeSeries[currentTimeSeriesItemIndex],
        limits: state.currentTimeSeries[currentTimeSeriesItemIndex]?.limits?.map(limit => {
            if (limit.uniqId === limitUniqId) {
                const updatedLimit = { ...limit, [field]: value };
                const initialLimit = correspondingTimeSeriesItem?.limits?.find(initialLimit => initialLimit.uniqId === limitUniqId);
                const isModified = !isEqual(omit(updatedLimit, 'isModified'), omit(initialLimit, 'isModified'));
                return { ...updatedLimit, isModified };
            }
            return limit;
        })
    };

    if (correspondingTimeSeriesItem !== undefined) {
        // Compare with correspondingTimeSeriesItem to determine isModified
        const isModified = !isEqual(
            updatedCurrentTimeSeries?.limits?.map(limit => {
                return {
                    ...limit,
                    isModified: undefined // Exclude isModified from the comparison
                };
            }),
            correspondingTimeSeriesItem?.limits?.map(limit => ({
                ...limit,
                isModified: undefined // Exclude isModified from the comparison
            }))
        );
        // Update isModified in updatedCurrentTimeSeries
        updatedCurrentTimeSeries.isModified = isModified;
    } else {
        updatedCurrentTimeSeries.isModified = true;
    }

    const updatedTimeSeries = checkAnyLimitValueIsSame(updatedCurrentTimeSeries);

    return { updatedTimeSeries, currentTimeSeriesItemIndex };
};

export const updateSuggestedLimitReducerData = (state, action) => {
    const { limitUniqId, field, value } = action.payload;
    const updatedTimeSeries = {
        ...state.selectedTimeSeriesForSuggestion,
        limits: state.selectedTimeSeriesForSuggestion?.limits?.map((l) => {
            if (l.uniqId === limitUniqId) {
                return {
                    ...l,
                    [field]: value,
                    isModified: true
                };
            }
            return l;
        })
    };
    return { updatedTimeSeries };
};

export const updateSelectedTimeSeriesInListReducerData = (state, action) => {
    const signal = action.payload;
    const { suggestedLimit } = state;
    const suggestedLimits = suggestedLimit?.limits || [];
    const currentLimits = signal.limits?.filter((lmt) => lmt?.limitId && !lmt?.isUserLimit) || [];
    const updatedTimeSeriesList = state.currentTimeSeries?.map((ct) => {
        if (ct.uniqId !== signal.uniqId) {
            return ct; // Return unchanged if uniqId doesn't match
        }
        const updatedLimits =
            action.selectedLimitType === limitTypes[1]
                ? suggestedLimits.map((tsl, i) => {
                    const currentLimit = currentLimits[i];
                    return currentLimit
                        ? {
                            ...currentLimit,
                            limitValue: tsl?.limitValue,
                            limitLevel: tsl?.limitLevel,
                            limitDirection: tsl?.limitDirection,
                            isModified: true,
                        }
                        : { ...tsl, isModified: true };
                })
                : [...currentLimits];
        const personalLimits = ct?.limits?.filter((lmt) => lmt?.isUserLimit) || [];
        return checkAnyLimitValueIsSame({
            ...signal,
            isModified: true,
            limits: [...personalLimits, ...updatedLimits],
        });
    });

    return { updatedTimeSeriesList };
};

export const mergeSuggestedLimits = (payload) => {
    if (!payload || !payload.calculatedLimit) {
        return [];
    }
    const limitMappings = [
        { key: 'highErrorThreshold', limitLevel: 'Alarm', limitDirection: 'UpperLimit' },
        { key: 'lowErrorThreshold', limitLevel: 'Alarm', limitDirection: 'LowerLimit' },
        { key: 'highWarningThreshold', limitLevel: 'Alert', limitDirection: 'UpperLimit' },
        { key: 'lowWarningThreshold', limitLevel: 'Alert', limitDirection: 'LowerLimit' },
    ];
    return limitMappings
        .filter(({ key }) => payload.calculatedLimit[key]) // Only include limits with valid values
        .map(({ key, limitLevel, limitDirection }) => ({
            uniqId: crypto.randomUUID(),
            limitLevel,
            limitValue: payload.calculatedLimit[key],
            limitDirection,
            key,
        }));
};

export const handleUnitConversionSuggestedLimits = (timeseries, suggestedLimits) => {
    const userMeasurementUnitGroups = getMeasurementUnits();
    const precision = timeseries?.timeseriesPrecision ?? timeseries?.precision ?? DEFAULT_PRECISION;
    const convertedLimits = suggestedLimits?.map((lmt) => {
        const valueObj = baseToCustomUOM?.convertAndFormatWithGroups(
            timeseries?.timeseriesUnitGroupID,
            lmt?.limitValue,
            userMeasurementUnitGroups);
        return {
            ...lmt,
            limitValue: valueObj?.measurementValue % 1 === 0 ? valueObj?.measurementValue : valueObj?.measurementValue?.toFixed(precision)
        };
    });
    return { convertedLimits };
};

export const handleDiscardLimits = (state, payload) => {
    const { currentTimeSeriesUniqId, isUserLimit } = payload;

    // Find the index of the currentTimeSeries item to update
    const currentTimeSeriesItemIndex = state.currentTimeSeries.findIndex(item => item.uniqId === currentTimeSeriesUniqId);
    const currentTimeSeriesItem = state.currentTimeSeries?.find(item => item?.uniqId === currentTimeSeriesUniqId);

    // Find the corresponding item in timeSeriesList for comparison
    const correspondingTimeSeriesItem = state.timeSeries.timeSeriesList.find(item => item.uniqId === currentTimeSeriesUniqId);
    let updatedCurrentTimeSeries = correspondingTimeSeriesItem || currentTimeSeriesItem;

    if (correspondingTimeSeriesItem) {
        // If correspondingTimeSeriesItem exists, copy it back to currentTimeSeries
        updatedCurrentTimeSeries = {
            ...currentTimeSeriesItem,
            limits: currentTimeSeriesItem?.limits?.map((ctl) => {
                if (ctl?.isUserLimit === isUserLimit) {
                    const oldLimit = correspondingTimeSeriesItem?.limits?.find((lmt) => ctl?.uniqId === lmt?.uniqId);
                    if (oldLimit) {
                        return { ...oldLimit, isModified: false };
                    } else {
                        return {
                            ...ctl,
                            limitValue: '',
                            limitLevel: '',
                            limitDirection: '',
                            isModified: false
                        };
                    }
                } else {
                    return ctl;
                }
            })?.filter((item) => item?.uniqId),
            // isModified: correspondingTimeSeriesItem?.limits?.find((lmt) => lmt?.isModified) ? true : false // Assuming the copied item is not modified
        };
    } else {
        // If correspondingTimeSeriesItem does not exist, reset limit values to empty string
        updatedCurrentTimeSeries = {
            ...state.currentTimeSeries[currentTimeSeriesItemIndex],
            limits: state.currentTimeSeries[currentTimeSeriesItemIndex]?.limits.map(limit => {
                if (isUserLimit === limit?.isUserLimit) {
                    return {
                        ...limit,
                        limitValue: '',
                        limitLevel: '',
                        limitDirection: '',
                        isModified: false
                    };
                } else {
                    return limit;
                }
            }),
            // isModified: state.currentTimeSeries[currentTimeSeriesItemIndex]?.limits?.find((l) => l?.isModified) ? true : false
        };
    }

    updatedCurrentTimeSeries = {
        ...updatedCurrentTimeSeries,
        isModified: updatedCurrentTimeSeries?.limits?.find((l) => l?.isModified || l?.limitValue === '')
    };

    return updatedCurrentTimeSeries;
};

export const handleDefaultUnitConversion = (response, limitData) => {
    const userMeasurementUnitGroups = getMeasurementUnits();
    response?.timeseries?.map((ts) => {
        const precision = ts?.timeseriesPrecision ?? ts?.precision ?? DEFAULT_PRECISION;
        const timeseriesUnitGroupID = limitData?.timeseriesUnitGroupID;
        const unit = getMeasurementUnitSymbol(timeseriesUnitGroupID);
        ts.limits?.map((lmt) => {
            const valueObj = baseToCustomUOM?.convertAndFormatWithGroups(
                timeseriesUnitGroupID,
                lmt?.defaultValue,
                userMeasurementUnitGroups);
            lmt.defaultValue = valueObj?.measurementValue % 1 === 0 ? valueObj?.measurementValue : valueObj?.measurementValue?.toFixed(precision);
        });
        ts.unit = unit;
    });

    return response;
};

export const updateDefaultLimitsToCurrentLimits = (state, action) => {
    const signal = action.data;
    const updatedTimeSeriesList = state.currentTimeSeries?.map((ct) => {
        if (ct.uniqId === signal.uniqId) {
            return { ...signal, isModified: true };
        }
        return ct;
    });
    return { updatedTimeSeriesList };
};
